import { useMemo, useState, useEffect } from "react";
import type { TimerProfileFormValues, DaysActive } from "../../components/types";

export type TimerProfileValidationErrors = Record<string, string>;

const TIMER_PROFILE_FIELD_CONFIG: Record<string, { min: number; max: number }> = {
  dimLevel: { min: 0, max: 100 },
  brightLevel: { min: 0, max: 100 },
};

const PAIRED_TIMER_FIELDS: Record<string, string[]> = {
  dimLevel: ["brightLevel"],
  brightLevel: ["dimLevel"],
};

export function sanitizeNumericInput(raw: any, min: number, max: number): number {
  const trimmed = String(raw).trim();

  if (trimmed === "") return min;

  if (trimmed.startsWith("-")) return min;

  const digitsOnly = trimmed.replace(/[^0-9]/g, "");
  if (digitsOnly === "") return min;

  const withoutLeadingZeros = digitsOnly.replace(/^0+/, "") || "0";
  const num = Number(withoutLeadingZeros);
  if (isNaN(num)) return min;

  return Math.min(Math.max(num, min), max);
}

export function computeTimerProfileErrors(values: TimerProfileFormValues): TimerProfileValidationErrors {
  const errs: TimerProfileValidationErrors = {};

  if (!values.name.trim()) {
    errs.name = "Profile name is required";
  }

  if (!values.startTime) {
    errs.startTime = "Start time is required";
  }

  if (!values.endTime) {
    errs.endTime = "End time is required";
  }

  if (!values.days || values.days.length === 0) {
    errs.days = "At least one day must be selected";
  }

  if (!values.actionMode) {
    errs.actionMode = "Action mode is required";
  }

  if (values.zoneId === null || values.zoneId === undefined) {
    errs.zoneId = "Group is required";
  }

  if (values.dimLevel !== null && (values.brightLevel === null || values.brightLevel === undefined)) {
    errs.brightLevel = "Bright Level is required";
  }
  if (values.brightLevel !== null && (values.dimLevel === null || values.dimLevel === undefined)) {
    errs.dimLevel = "Dim Level is required";
  }

  if (
    values.dimLevel !== null &&
    values.brightLevel !== null &&
    values.brightLevel <= values.dimLevel
  ) {
    errs.brightLevel = "Bright Level must be greater than Dim Level";
  }

  const lc = TIMER_PROFILE_FIELD_CONFIG;
  if (values.dimLevel !== null && (values.dimLevel < lc.dimLevel.min || values.dimLevel > lc.dimLevel.max)) {
    errs.dimLevel = `Value must be between ${lc.dimLevel.min} and ${lc.dimLevel.max}`;
  }
  if (values.brightLevel !== null && (values.brightLevel < lc.brightLevel.min || values.brightLevel > lc.brightLevel.max)) {
    errs.brightLevel = `Value must be between ${lc.brightLevel.min} and ${lc.brightLevel.max}`;
  }

  return errs;
}

export function useTimerProfileValidation(values: TimerProfileFormValues) {
  const [errors, setErrors] = useState<TimerProfileValidationErrors>({});
  const [visibleErrorFields, setVisibleErrorFields] = useState<Set<string>>(new Set());
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const touchField = (fieldName: string) => {
    setVisibleErrorFields((prev) => {
      const next = new Set(prev);
      next.add(fieldName);
      const paired = PAIRED_TIMER_FIELDS[fieldName];
      if (paired) {
        paired.forEach((f) => next.add(f));
      }
      return next;
    });
  };

  const validate = () => {
    const allFields = [
      "name",
      "startTime",
      "endTime",
      "days",
      "actionMode",
      "zoneId",
      "dimLevel",
      "brightLevel",
    ];
    setHasSubmitted(true);
    setVisibleErrorFields(new Set(allFields));
    const errs = computeTimerProfileErrors(values);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  useEffect(() => {
    const errs = computeTimerProfileErrors(values);
    setErrors(errs);
  }, [values]);

  const displayedErrors = useMemo(() => {
    const result: TimerProfileValidationErrors = {};
    if (!hasSubmitted) {
      return result;
    }

    visibleErrorFields.forEach((field) => {
      if (errors[field]) {
        result[field] = errors[field];
      }
    });
    return result;
  }, [errors, visibleErrorFields]);

  const setFieldError = (fieldName: string, message: string) => {
    setErrors((prev) => ({ ...prev, [fieldName]: message }));
    setVisibleErrorFields((prev) => new Set(prev).add(fieldName));
  };

  const reset = () => {
    setErrors({});
    setVisibleErrorFields(new Set());
    setHasSubmitted(false);
  };

  return {
    errors,
    visibleErrorFields,
    displayedErrors,
    touchField,
    validate,
    setFieldError,
    reset,
  };
}
